import axios, { AxiosInstance } from 'axios';
import { appInsights } from './app-insights';
import { OktaAuth } from '@okta/okta-auth-js';
import { oktaConfig } from './oktaConfig';

type Headers = {
    Authorization: string;
    'x-profile-test'?: string;
    'Content-Type'?: string;
};

const oktaAuth = new OktaAuth(oktaConfig)

export default class fetchWrapper {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create();

        this.axiosInstance.interceptors.response.use(
            response => response,
            async error => {
                const originalRequest = error.config;
                appInsights.trackException({
                    exception: error,
                    properties: {
                        message: "HTTP request failed",
                        url: originalRequest?.url || "Unknown URL",
                        method: originalRequest?.method || "Unknown Method",
                        status: error.response?.status || "No Status",
                    },
                });

                if (error.response?.status === 401) {
                    await oktaAuth.signInWithRedirect();
                    return Promise.reject(error);
                }

                return Promise.reject(error);
            }
        );
    }

    private async getHeaders(additionalHeaders: Partial<Headers> = {}): Promise<Headers> {
        const accessToken = oktaAuth.getAccessToken();
        const headers: Headers = {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...additionalHeaders
        };

        if (import.meta.env.VITE_TEST) {
            const mockProfile = localStorage.getItem('profile');
            if (mockProfile) {
                headers['x-profile-test'] = mockProfile;
            }
        }

        return headers;
    }

    async get(url: string, headers: Partial<Headers> = {}) {
        const requestHeaders = await this.getHeaders(headers);
        return this.axiosInstance.get(url, { headers: requestHeaders });
    }

    async post(url: string, body: any, headers: Partial<Headers> = {}, serialize: boolean = true) {
        const requestHeaders = await this.getHeaders({
            'Content-Type': 'application/json',
            ...headers
        });
        if ('lng' in body) {
            const lng = body.lng;
            delete body.lng;
            return this.axiosInstance.post(url, serialize ? JSON.stringify(body) : body, { headers: requestHeaders, params: { lng } });
        }
        return this.axiosInstance.post(url, serialize ? JSON.stringify(body) : body, { headers: requestHeaders });
    }

    async put(url: string, body: any, headers: Partial<Headers> = {}, serialize: boolean = true) {
        const requestHeaders = await this.getHeaders({
            'Content-Type': 'application/json',
            ...headers
        });
        return this.axiosInstance.put(url, serialize ? JSON.stringify(body) : body, { headers: requestHeaders });
    }

    async delete(url: string, headers: Partial<Headers> = {}) {
        const requestHeaders = await this.getHeaders(headers);
        return this.axiosInstance.delete(url, { headers: requestHeaders });
    }

    async getProfile(uid: string) {
        const user = await this.get(`/api/profile/${uid}`);
        return {
            data: {
                givenName: user.data.firstName || '',
                surname: user.data.lastName || '',
                mail: user.data.email || '',
                officeLocation: user.data.officeLocation || '',
                employeeId: user.data.employeeId || '',
                department: user.data.department || '',
                companyName: user.data.companyName || ''
            }
        };
    }

    async fetchAccessToken(): Promise<{ idToken: string, accessToken: string }> {
        try {
            const tokens = await oktaAuth.tokenManager.getTokens();
            return {
                idToken: tokens.idToken?.idToken || '',
                accessToken: tokens.accessToken?.accessToken || ''
            };
        } catch (error) {
            appInsights.trackException({
                exception: error as Error,
                properties: {
                    message: "Failed to acquire token",
                },
            });
            await oktaAuth.signInWithRedirect();
            return {
                idToken: '',
                accessToken: ''
            };
        }
    }

    async getDocument(url: string) {
        const headers = await this.getHeaders();
        return this.axiosInstance.get(url, { headers, responseType: 'blob' });
    }

    async sendFeedback(url: string, feedback: string | null, screenshot: string | null) {
        return this.post(url, { feedback, screenshot });
    }

    async getFeedback(url: string) {
        return this.get(url);
    }

    async getInteractionsFilters(url: string, filterType: string) {
        const headers = await this.getHeaders();
        return this.axiosInstance.get(url, { headers, params: { filterType } });
    }

    async getInteractions(url: string, params: Record<string, any> = {}) {
        const headers = await this.getHeaders();
        return this.axiosInstance.get(url, { headers, params });
    }

    async getUserRoles(url: string) {
        return this.get(url);
    }

    async createUserRole(url: string, email: string, role: string) {
        return this.post(url, { email, role });
    }

    async editUserRole(url: string, email: string, newRole: string) {
        return this.put(`${url}/${email}`, { role: newRole });
    }

    async deleteUserRole(url: string, email: string) {
        return this.delete(`${url}/${email}`);
    }

    async getApps(url: string) {
        return this.get(url);
    }

    async getAppSecret(url: string, appId: string) {
        return this.get(`${url}/${appId}/secret`);
    }

    async createApp(url: string, name: string) {
        return this.post(url, { name });
    }

    async deleteApp(url: string, appId: string) {
        return this.delete(`${url}/${appId}`);
    }

    async sendFileData(url: string, file: File, data: { language: string; topic: string; properties: string[] }) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('data', JSON.stringify(data));

        return this.post(url, formData, { 'Content-Type': 'multipart/form-data' }, false);
    }

    async getFiles(url: string) {
        return this.get(url);
    }

    async searchFilesByName(url: string, fileName: string) {
        const headers = await this.getHeaders();
        return this.axiosInstance.get(url, { headers, params: { name: fileName } });
    }

    async replaceFile(url: string, file: File, replaceData: { RowKey: string; PartitionKey: string }) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('data', JSON.stringify(replaceData));

        return this.post(url, formData, { 'Content-Type': 'multipart/form-data' }, false);
    }

    async deleteFile(url: string, deleteData: { RowKey: string; PartitionKey: string; FileName: string; FileUrl: string }) {
        return this.post(url, deleteData, { 'Content-Type': 'application/json' });
    }

    async checkPiiFile(url: string, file: File) {
      const formData = new FormData();
      formData.append('file', file);

      const response: any = await this.post(url, formData, { 'Content-Type': 'multipart/form-data' }, false);

      const flatPiiData = response.data.results.flatMap((result: any) =>
        result.entities.map((entity: any) => ({
          detectedText: entity.text,
          category: Object.entries(entity.score_dictionary)[0][0],
          page: entity.additional_metadata.page_number,
          startOffset: entity.additional_metadata.start_offset,
          length: entity.additional_metadata.length,
        }))
      );

      return {
        flatPiiData,
        rawResults: response.data.results,
      };
    }

    async fetchRecords(url: string) {
        return this.get(url);
    }

    async addRecord(url: string, recordData: any) {
        return this.post(url, recordData);
    }

    async editRecord(url: string, recordData: any) {
        return this.put(url, { RowKey: recordData.RowKey, PartitionKey: "0", data: recordData.data });
    }

    async deleteRecord(url: string) {
        return this.delete(url);
    }

    async updateFileProperties(rowKey: string, properties: string[]) {
        return this.put(`/api/files/${rowKey}`, { properties });
    }

    async getProperties() {
        return this.fetchRecords("/api/config/props/records");
    }

    async addProperty(property: { Name: string; Description?: string; Type?: string }) {
        return this.addRecord("/api/config/props/add", property);
    }

    async deleteProperty(rowKey: string) {
        return this.deleteRecord(`/api/config/props/remove/${rowKey}`);
    }

    async getChatStatus(): Promise<{ chat_status: boolean }> {
        return this.get("/api/chat-status").then(res => res.data);
    }
}
